import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { media } from "../../../helpers/responsive";
import { useComparisonData } from "../hooks/useComparisonData";
import ComparisonChart from "../components/ComparisonChart";
import ComparisonDimensionChart from "../components/ComparisonDimensionChart";
import ComparisonTable from "../components/ComparisonTable";
import ComparisonLoader from "../components/ComparisonLoader";
import type { ComparisonWidget, DimensionWidget, DimensionMetric } from "../types";

const RETRY_DEBOUNCE_MS = 3000;

const ComparisonView = () => {
  const { data, isLoading, error, refetch } = useComparisonData();
  const chartContainerRef = useRef<HTMLElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [retrying, setRetrying] = useState(false);
  const retryTimerRef = useRef<NodeJS.Timeout | null>(null);

  // useLayoutEffect fires before paint — containerWidth is correct before charts are visible.
  useLayoutEffect(() => {
    const measure = () => {
      const wrapper = chartContainerRef.current;
      if (!wrapper) return;
      const { paddingLeft, paddingRight } = window.getComputedStyle(wrapper);
      setContainerWidth(wrapper.clientWidth - parseFloat(paddingLeft) - parseFloat(paddingRight));
    };
    measure();
    let lastInnerW = window.innerWidth;
    const handleResize = () => {
      if (window.innerWidth !== lastInnerW) {
        lastInnerW = window.innerWidth;
        measure();
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleRetry = useCallback(() => {
    if (retrying) return;
    setRetrying(true);
    refetch();
    retryTimerRef.current = setTimeout(() => setRetrying(false), RETRY_DEBOUNCE_MS);
  }, [refetch, retrying]);

  const baseWidget = useMemo<ComparisonWidget | null>(() => {
    if (data?.widgets?.[0]) {
      return data.widgets[0] as ComparisonWidget;
    }
    return null;
  }, [data]);

  const dimensionWidget = useMemo<DimensionWidget | null>(() => {
    if (data?.widgets?.[1]) {
      return data.widgets[1] as DimensionWidget;
    }
    return null;
  }, [data]);

  const comparisonMode = data?.comparison_mode;
  const entityId = data?.entity_id;

  if (isLoading) {
    return (
      <Wrapper
        ref={chartContainerRef}
        data-comparison-mode={comparisonMode}
        data-entity-id={entityId}
        data-loading="true"
      >
        <ComparisonLoader />
      </Wrapper>
    );
  }

  if (error) {
    return (
      <Wrapper
        data-comparison-mode={comparisonMode}
        data-entity-id={entityId}
        data-loading="false"
      >
        <div className="error-container">
          <div className="error-content">
            <p className="error-text">Failed to load comparison data.</p>
            <button
              className="retry-btn"
              onClick={handleRetry}
              disabled={retrying}
            >
              {retrying ? "Retrying..." : "Retry"}
            </button>
          </div>
        </div>
      </Wrapper>
    );
  }

  if (!data || !baseWidget) return null;

  return (
    <Wrapper
      ref={chartContainerRef}
      data-comparison-mode={comparisonMode}
      data-entity-id={entityId}
      data-loading="false"
    >
      {dimensionWidget && Array.isArray(dimensionWidget.widget_data) && (
        <section className="card-section">
          <h2 className="section-title">{dimensionWidget.title}</h2>
          <ComparisonDimensionChart
            title={dimensionWidget.title}
            metrics={dimensionWidget.widget_data as DimensionMetric[]}
            width={containerWidth - 50}
          />
        </section>
      )}

      {baseWidget && (
        <>
          <section className="card-section">
            <h2 className="section-title">{baseWidget.title}</h2>
            <ComparisonChart
              widgetData={baseWidget.widget_data}
              width={containerWidth - 50}
            />
          </section>

          <section className="card-section">
            <ComparisonTable widgetData={baseWidget.widget_data} />
          </section>
        </>
      )}
    </Wrapper>
  );
};

export default ComparisonView;

const Wrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 16px 24px 40px;
  display: flex;
  flex-direction: column;
  gap: 20px;

  .card-section {
    background-color: ${({ theme }) => theme.widget.background};
    border-radius: 20px;
    padding: 25px;
    filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.06));
  }

  .section-title {
    font-size: 22px;
    font-weight: 600;
    margin: 0 0 16px 0;
    color: ${({ theme }) => theme.text};
  }

  .error-container {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 50vh;
  }

  .error-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    background: ${({ theme }) => theme.widget.background};
    border-radius: 20px;
    padding: 48px 40px;
    filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.06));
  }

  .error-text {
    color: #c62828;
    font-size: 15px;
    font-weight: 500;
    margin: 0;
  }

  .retry-btn {
    padding: 10px 32px;
    border: none;
    border-radius: 8px;
    background: #0D8A6A;
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s;

    &:hover:not(:disabled) {
      background: #0a7359;
    }

    &:disabled {
      opacity: 0.6;
      cursor: default;
    }
  }

  ${media.down.sm} {
    padding: 12px 12px 24px;
    gap: 12px;

    .card-section {
      padding: 16px;
      border-radius: 14px;
    }

    .section-title {
      font-size: 18px;
    }
  }

  ${media.down.xs} {
    padding: 10px 8px 16px;

    .card-section {
      padding: 12px;
      border-radius: 12px;
    }
  }

  @media (max-width: 719px) {
    padding-top: 60px;
  }
`;
